import React, { useMemo } from "react";
import {
  Activity,
  Loader2,
  ShieldAlert,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import { formatOdds, formatRelative } from "../../utils/bettorsDeskHelpers";
import {
  formatTicketCount,
  liabilityConcentration,
  normalizeTicketPulse,
  oddsHistogram,
} from "../../utils/ticketsDeskHelpers";
import {
  TicketHouseFlow,
  TicketLiabilityMeter,
  TicketOddsHistogram,
  TicketStakeRange,
  TicketVolumeCombo,
} from "./TicketChartViz";

const RiskHero = ({
  exposure,
  openCount,
  filtered,
  concentration,
  biggest,
  compact = false,
}) => (
  <div
    className={`bd-risk-hero ${exposure > 0 ? "bd-risk-hero-live" : ""} ${
      compact ? "bd-risk-hero-compact" : ""
    }`}
  >
    <div className="bd-risk-hero-main">
      <span className="bd-risk-kicker">
        Open exposure{filtered ? " · house-wide" : ""}
      </span>
      <strong
        className={`bd-risk-hero-value tabular-nums ${
          exposure > 0 ? "bd-tone-down" : "bd-tone-up"
        }`}
      >
        {formatCash(exposure, { compact: true })}
      </strong>
      {!compact ? (
        <span className="bd-risk-hero-sub">
          {exposure > 0
            ? `What you pay if every open ticket wins · ${openCount} open ticket${
                openCount === 1 ? "" : "s"
              }`
            : "No open liability — every ticket is settled."}
        </span>
      ) : null}
    </div>

    {compact ? (
      <>
        <span className="bd-risk-flag tabular-nums">
          {openCount} open
        </span>
        {concentration.loaded && concentration.sharePct > 0 ? (
          <span
            className={`bd-risk-flag ${
              concentration.risky ? "bd-risk-flag-warn" : "bd-risk-flag-ok"
            }`}
          >
            Top ticket · {concentration.sharePct}%
          </span>
        ) : null}
      </>
    ) : (
      <>
        <div className="bd-risk-hero-tiles">
          <div className="bd-risk-tile">
            <span>Open tickets</span>
            <strong className="tabular-nums">{openCount}</strong>
          </div>
          <div className="bd-risk-tile">
            <span>Avg open liability</span>
            <strong className="tabular-nums">
              {openCount > 0 ? formatCash(exposure / openCount, { compact: true }) : "—"}
            </strong>
          </div>
          <div className="bd-risk-tile bd-risk-tile-wide">
            <span>Biggest single liability</span>
            {biggest ? (
              <>
                <strong className="tabular-nums bd-tone-down">
                  {formatCash(biggest.liability, { compact: true })}
                </strong>
                <em>
                  {biggest.username || "Bettor"} · {biggest.legCount || 0} leg
                  {Number(biggest.legCount) === 1 ? "" : "s"} ·{" "}
                  {formatOdds(biggest.totalOddsValue)} ·{" "}
                  {formatRelative(biggest.createdAt)}
                </em>
              </>
            ) : (
              <strong>—</strong>
            )}
          </div>
        </div>

        {concentration.loaded ? (
          <TicketLiabilityMeter concentration={concentration} topTicket={biggest} />
        ) : null}
      </>
    )}
  </div>
);

/**
 * Risk desk — analytical board for the tickets desk.
 * layout="workspace" — horizontal strip above ticket inspect panel (default).
 */
const TicketAnalyticsStrip = ({
  pulseRaw,
  loading = false,
  filtered = false,
  housePulseRaw = null,
  highlights = null,
  rows = [],
  hasMorePages = false,
  layout = "workspace",
}) => {
  const pulse = useMemo(() => normalizeTicketPulse(pulseRaw), [pulseRaw]);
  const housePulse = useMemo(() => normalizeTicketPulse(housePulseRaw), [housePulseRaw]);
  const isWorkspace = layout === "workspace";

  const topByLiability = useMemo(
    () => (Array.isArray(highlights?.topByLiability) ? highlights.topByLiability : []),
    [highlights]
  );
  const biggest = topByLiability[0] || null;

  const exposurePulse = housePulse.loaded ? housePulse : pulse;
  const concentration = useMemo(
    () => liabilityConcentration(exposurePulse.pendingLiability, biggest),
    [exposurePulse.pendingLiability, biggest]
  );

  const histogram = useMemo(() => oddsHistogram(rows), [rows]);

  if (loading) {
    return (
      <section
        className={`bd-ticket-analytics bd-riskdesk bd-riskdesk-${layout}`}
        aria-label="Risk desk"
      >
        <div className="bd-ticket-analytics-loading">
          <Loader2 size={14} className="animate-spin" />
          <span>Reading risk…</span>
        </div>
      </section>
    );
  }

  if (!pulse.loaded) {
    return null;
  }

  const ticketCount = pulse.ticketCount;
  const dailyHasVolume =
    pulse.dailyTrend?.some((day) => Number(day.count) > 0 || Number(day.sumStake) > 0) ?? false;
  const exposure = exposurePulse.pendingLiability;
  const openCount = exposurePulse.openTicketCount;
  const loadedRowsNote = hasMorePages ? "loaded pages" : "loaded rows";

  return (
    <section
      className={`bd-ticket-analytics bd-riskdesk bd-riskdesk-${layout}`}
      aria-label="Risk desk"
    >
      {!isWorkspace ? (
        <header className="bd-ticket-analytics-head">
          <ShieldAlert size={14} />
          <div>
            <h2>Risk desk</h2>
            <p>
              {ticketCount > 0
                ? `${formatTicketCount(ticketCount)}${
                    filtered ? " matching filters" : " on this house"
                  } · exposure, volume and book profile from stored records.`
                : "Risk analytics appear once tickets match your filters."}
            </p>
          </div>
        </header>
      ) : null}

      {ticketCount === 0 ? (
        <>
          <RiskHero
            exposure={exposure}
            openCount={openCount}
            filtered={filtered}
            concentration={concentration}
            biggest={biggest}
            compact={isWorkspace}
          />
          <p className="bd-audience-empty bd-ticket-analytics-empty">
            No tickets in this selection yet.
          </p>
        </>
      ) : (
        <>
          <RiskHero
            exposure={exposure}
            openCount={openCount}
            filtered={filtered}
            concentration={concentration}
            biggest={biggest}
            compact={isWorkspace}
          />

          <div className="bd-riskdesk-grid">
            <article className="bd-ticket-analytics-card bd-riskdesk-card-volume">
              <header className="bd-book-card-head">
                <TrendingUp size={11} aria-hidden />
                <div>
                  <span className="bd-book-kicker">Volume · 7d</span>
                  {!isWorkspace ? (
                    <em className="bd-book-caption">Tickets placed vs staked</em>
                  ) : null}
                </div>
              </header>
              {pulse.dailyTrend && dailyHasVolume ? (
                <TicketVolumeCombo
                  items={pulse.dailyTrend}
                  ariaLabel="Tickets placed and amount staked over the last seven days"
                />
              ) : (
                <p className="bd-audience-empty bd-book-empty">
                  No volume in 7d
                </p>
              )}
            </article>

            <article className="bd-ticket-analytics-card">
              <header className="bd-book-card-head">
                <Activity size={11} aria-hidden />
                <div>
                  <span className="bd-book-kicker">Odds profile</span>
                  {!isWorkspace ? (
                    <em className="bd-book-caption">{loadedRowsNote}</em>
                  ) : null}
                </div>
              </header>
              {histogram.loaded ? (
                <TicketOddsHistogram
                  histogram={histogram}
                  ariaLabel="Ticket count by total odds bucket"
                />
              ) : (
                <p className="bd-audience-empty bd-book-empty">—</p>
              )}
            </article>

            <article className="bd-ticket-analytics-card">
              <header className="bd-book-card-head">
                <Wallet size={11} aria-hidden />
                <div>
                  <span className="bd-book-kicker">Status flow</span>
                  {!isWorkspace ? (
                    <em className="bd-book-caption">House view</em>
                  ) : null}
                </div>
              </header>
              {pulse.byStatus ? (
                <TicketHouseFlow
                  byStatus={pulse.byStatus}
                  ariaLabel="Ticket settlement status from the house's perspective"
                />
              ) : (
                <p className="bd-audience-empty bd-book-empty">—</p>
              )}
            </article>

            <article className="bd-ticket-analytics-card bd-ticket-analytics-card-metrics">
              <header className="bd-book-card-head">
                <Users size={11} aria-hidden />
                <div>
                  <span className="bd-book-kicker">Selection</span>
                  {!isWorkspace ? (
                    <em className="bd-book-caption">
                      {filtered ? "Filtered" : "House-wide"}
                    </em>
                  ) : null}
                </div>
              </header>

              {!isWorkspace && pulse.minStake != null && pulse.maxStake != null ? (
                <TicketStakeRange
                  minStake={pulse.minStake}
                  avgStake={pulse.avgStake}
                  maxStake={pulse.maxStake}
                />
              ) : null}

              <dl className="bd-ticket-metrics">
                <div>
                  <dt>Avg stake</dt>
                  <dd className="tabular-nums">
                    {pulse.avgStake != null ? formatCash(pulse.avgStake, { compact: true }) : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Avg odds</dt>
                  <dd className="tabular-nums">
                    {pulse.avgOdds != null ? formatOdds(pulse.avgOdds) : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Avg legs</dt>
                  <dd className="tabular-nums">
                    {pulse.avgLegCount != null ? pulse.avgLegCount.toFixed(1) : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Bettors</dt>
                  <dd className="tabular-nums">{pulse.distinctBettors || "—"}</dd>
                </div>
                {!isWorkspace ? (
                  <>
                    <div>
                      <dt>Repeat</dt>
                      <dd className="tabular-nums">{pulse.repeatBettorCount}</dd>
                    </div>
                    <div>
                      <dt>Tix / bettor</dt>
                      <dd className="tabular-nums">
                        {pulse.avgTicketsPerBettor != null
                          ? pulse.avgTicketsPerBettor.toFixed(1)
                          : "—"}
                      </dd>
                    </div>
                  </>
                ) : null}
              </dl>
            </article>
          </div>
        </>
      )}
    </section>
  );
};

export default TicketAnalyticsStrip;
